import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Tooltip,
  Typography,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import InfoIcon from '@mui/icons-material/Info';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { ImageRenderer } from 'components/DataPipeline/DataPipelineFormatter';
import WidgetMoreActionMenu from 'components/Dropdown/WidgetMoreActionMenu';
import { ReactComponent as ContactIcon } from '../../../assets/PepWise2.0/contact-phone-black.svg';
import { ReactComponent as CommingSoonIcon } from '../../../assets/coming-soon-dp.svg';

type LegendItem = {
  txt: string;
  colorCode: string;
};

type ContactInfo = {
  name?: string;
  email?: string;
  phone?: string;
};

type MoreActionItem = {
  key: string;
  label: string;
  icon?: JSX.Element;
  onMenuClick: (event: React.MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
};

type CustomAccordionProps = {
  id: string;
  title: React.ReactNode;
  titleTooltip?: React.ReactNode;
  expanded: boolean;
  onChange?: (event: React.SyntheticEvent, expanded: boolean) => void;

  isActive?: boolean;
  statusLabel?: string;
  etaLabel?: React.ReactNode;

  contactInfo?: ContactInfo | null;
  statusImageUrl?: string | null;

  legendData?: LegendItem[];
  moreActionItems?: MoreActionItem[];

  renderExpandedContent?: React.ReactNode;
  renderDetails?: React.ReactNode;
  headerAction?: React.ReactNode;

  colors: any;
};

export default function CustomAccordion({
  id,
  title,
  titleTooltip,
  expanded,
  onChange,
  isActive = true,
  statusLabel,
  etaLabel,
  contactInfo,
  statusImageUrl,
  legendData = [],
  moreActionItems = [],
  renderExpandedContent,
  renderDetails,
  headerAction,
  colors,
}: Readonly<CustomAccordionProps>) {
  const accordionHeaderColors = colors.accordionHeader;

  const hasContactInfo = Boolean(contactInfo?.name || contactInfo?.email || contactInfo?.phone);

  const showComingSoon = !isActive || statusLabel === 'Coming Soon';
  const showImage = Boolean(statusImageUrl) && !showComingSoon;

  const handleToggleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onChange?.(e as any, !expanded);
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={isActive ? onChange : undefined}
      sx={{
        boxShadow: '0px 0px 12px #00000024',
        border: 'none',
        backgroundImage: 'none',

        '&:before': {
          display: 'none',
        },

        '&.MuiPaper-root': {
          boxShadow: '0px 0px 12px #00000024',
        },

        '&.Mui-expanded': {
          boxShadow: '0px 0px 12px #00000024',
          mb: '10px !important',
        },
      }}
    >
      <AccordionSummary
        disableTouchRipple
        expandIcon={null}
        aria-controls={`${id}-content`}
        id={`${id}-header`}
        sx={{
          backgroundColor: `${accordionHeaderColors.background} !important`,
          minHeight:
            expanded && renderExpandedContent
              ? { xs: '500px', sm: '510px', md: '362px' }
              : { xs: 'auto', md: '60px' },
          height:
            expanded && renderExpandedContent
              ? { xs: '590px', sm: '510px', md: '362px' }
              : { xs: 'auto', md: '60px' },
          color: accordionHeaderColors.text,
          margin: '0px !important',
          p: '13px',
          alignItems: 'center',
          '& .MuiAccordionSummary-content': {
            margin: '0 !important',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          },
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignSelf: 'flex-start',
              justifyContent: 'space-between',
              width: '100%',
              gap: 2,
              flexShrink: 0,
              flexWrap: { xs: 'wrap', md: 'nowrap' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                minWidth: 0,
                flex: 1,
                overflow: 'hidden',
                flexWrap: { xs: 'wrap', sm: 'wrap', md: 'nowrap' },
                rowGap: 1,
              }}
            >
              {typeof title === 'string' ? (
                <Typography
                  variant="widgetTitle"
                  component="span"
                  sx={{
                    mr: titleTooltip ? '6px' : '15px',
                    whiteSpace: { xs: 'normal', md: 'nowrap' },
                    flexShrink: 0,
                    wordBreak: 'break-word',
                  }}
                >
                  {title}
                </Typography>
              ) : (
                <Box
                  sx={{
                    mr: titleTooltip ? '6px' : '15px',
                    whiteSpace: { xs: 'normal', md: 'nowrap' },
                    flexShrink: 0,
                    wordBreak: 'break-word',
                  }}
                >
                  {title}
                </Box>
              )}
              {titleTooltip && (
                <Tooltip
                  arrow
                  placement="top"
                  title={titleTooltip}
                  slotProps={{
                    tooltip: {
                      sx: {
                        backgroundColor:
                          `${accordionHeaderColors.tooltipBackground} !important`,
                        color:
                          accordionHeaderColors.tooltipText,
                        boxShadow:
                          '0px 4px 12px rgba(0, 0, 0, 0.15)',
                        borderRadius: '8px',
                        padding: '10px 12px',
                        maxWidth: 'min(650px, calc(100vw - 32px))',
                        width: 'max-content',
                        whiteSpacee: 'normal'
                      }
                    },
                    arrow: {
                      sx: {
                        color:
                        accordionHeaderColors.tooltipArrow,
                      },
                    },
                  }}
                >
                  <Box
                    component="span"
                    role="button"
                    tabIndex={0}
                    aria-label={`${title} sector mapping information`}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onFocus={(event) => {
                      event.stopPropagation();
                    }}
                    onKeyDown={(event) => {
                      event.stopPropagation();
                    }}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyConatent: 'center',
                      mr: '15px',
                      flexShrink: 0,
                      cursor: 'help',
                      color:
                        accordionHeaderColors.tooltipTitle,
                    }}
                  >
                    <InfoIcon
                      sx={{
                        width: '15px',
                        height: '15px',
                        fontSize: '15px',
                        color: 'inherit',
                      }}
                    />
                    </Box>
                </Tooltip>
              )}

              {hasContactInfo && (
                <Tooltip
                  arrow
                  slotProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: `${accordionHeaderColors.tooltipBackground} !important`,
                        color: accordionHeaderColors.tooltipText,
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                        borderRadius: '8px',
                        padding: '10px 12px',
                      },
                    },
                    arrow: {
                      sx: {
                        color: accordionHeaderColors.tooltipArrow,
                      },
                    },
                  }}
                  title={
                    <>
                      {contactInfo?.name && (
                        <>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '13px',
                              color: accordionHeaderColors.tooltipTitle,
                              fontWeight: 500,
                              fontStyle: 'italic',
                            }}
                          >
                            Contact Person
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '12px',
                              color: accordionHeaderColors.tooltipText,
                            }}
                          >
                            {contactInfo.name}
                          </Typography>
                        </>
                      )}

                      {contactInfo?.email && (
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '12px',
                            fontWeight: 500,
                            color: accordionHeaderColors.tooltipText,
                            fontStyle: 'italic',
                          }}
                        >
                          {contactInfo.email}
                        </Typography>
                      )}

                      {contactInfo?.phone && (
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '12px',
                            color: accordionHeaderColors.tooltipText,
                          }}
                        >
                          <PhoneIcon
                            sx={{
                              verticalAlign: 'middle',
                              width: '15px',
                              height: '15px',
                              color: 'blue',
                            }}
                          />{' '}
                          {contactInfo.phone}
                        </Typography>
                      )}
                    </>
                  }
                >
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      verticalAlign: 'middle',
                      mr: '15px',
                      flexShrink: 0,
                    }}
                  >
                    <ContactIcon />
                  </Box>
                </Tooltip>
              )}

              <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                {showComingSoon ? (
                  <Typography
                    sx={{
                      textAlign: 'center',
                      border: `1px solid ${accordionHeaderColors.comingSoonBorder}`,
                      borderRadius: '20px',
                      margin: '10px 0',
                      padding: '2px 5px',
                      display: 'inline-block',
                      fontSize: '12px',
                      backgroundColor: accordionHeaderColors.comingSoonBackground,
                      whiteSpace: 'nowrap',
                      color: colors.text.inverse,
                    }}
                  >
                    COMING SOON{' '}
                    {etaLabel && (
                      <Tooltip arrow title={etaLabel}>
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            verticalAlign: 'text-bottom',
                          }}
                        >
                          <CommingSoonIcon />
                        </Box>
                      </Tooltip>
                    )}
                  </Typography>
                ) : (
                  showImage && <ImageRenderer imageUrl={statusImageUrl as string} />
                )}
                {headerAction}
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'flex-end', sm: 'flex-end', md: 'center' },
                justifyContent: 'flex-end',
                flexShrink: 0,
                gap: 1,
                whiteSpace: 'nowrap',
                flexWrap: { xs: 'wrap', sm: 'wrap', md: 'nowrap' },
                width: { xs: '100%', sm: '100%', md: 'auto' },
                mt: { xs: 1, sm: 1, md: 0 },
                minWidth: 0,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {expanded && legendData.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: { xs: 'flex-end', sm: 'flex-end', md: 'flex-end' },
                    gap: 1,
                    mr: { xs: 0, sm: 0, md: 1 },
                    flexWrap: { xs: 'wrap', sm: 'wrap', md: 'nowrap' },
                    width: { xs: '100%', sm: '100%', md: 'auto' },
                  }}
                >
                  {legendData.map((legend) => (
                    <Typography
                      key={legend.txt}
                      sx={{
                        color: accordionHeaderColors.legendText,
                        fontSize: '14px',
                        fontWeight: 500,
                        display: 'inline-flex',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          borderRadius: '4px',
                          display: 'inline-block',
                          height: '15px',
                          mr: '6px',
                          width: '15px',
                          backgroundColor: legend.colorCode,
                          flexShrink: 0,
                        }}
                      />
                      {legend.txt}
                    </Typography>
                  ))}
                </Box>
              )}

              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
                onClick={handleToggleClick}
              >
                {expanded ? (
                  <KeyboardArrowUpIcon
                    sx={{ color: accordionHeaderColors.expandIconActive, fontSize: 24 }}
                  />
                ) : (
                  <KeyboardArrowDownIcon
                    sx={{ color: accordionHeaderColors.expandIconInactive, fontSize: 24 }}
                  />
                )}
              </Box>

              {moreActionItems.length > 0 && (
                <Box sx={{ flexShrink: 0 }}>
                  <WidgetMoreActionMenu menuItems={moreActionItems} />
                </Box>
              )}
            </Box>
          </Box>

          {expanded && renderExpandedContent && (
            <Box
              sx={{
                flex: 1,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 0,
                mt: { xs: 1.5, md: 0 },
              }}
            >
              {renderExpandedContent}
            </Box>
          )}
        </Box>
      </AccordionSummary>

      {renderDetails && (
        <AccordionDetails
          sx={{
            backgroundColor: 'transparent !important',
            color: `${colors.text.primary} !important`,
            padding: '0px',
            border: `none !important`,
            '&&': {
              border: 'none !important',
            },
          }}
        >
          {renderDetails}
        </AccordionDetails>
      )}
    </Accordion>
  );
}
